import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import type { AssistantCaseDetail, AssistantProposal } from '../contract'
import { assistantCasePath, formatAssistantFee } from './AssistantCaseCard'

const ROLE_LABELS: Record<AssistantCaseDetail['role'], string> = {
  referring: 'You referred this case',
  receiving: 'Referred to you',
  prospective: 'On the marketplace',
}

const ProposalRow = ({ proposal }: { proposal: AssistantProposal }) => {
  const who =
    [proposal.attorney, proposal.firm].filter(Boolean).join(', ') ||
    'Another firm'
  return (
    <Box
      data-testid="assistant-proposal-row"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {who}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[
            proposal.status,
            formatAssistantFee(proposal.fee),
            proposal.isInvitation ? 'Invited' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Box>
      {proposal.waitingOn === 'you' && (
        <Chip size="small" color="warning" label="Your move" />
      )}
    </Box>
  )
}

/** A single case at a glance, with the proposals on it and a link to open it. */
const AssistantCaseSummary = ({ data }: { data: AssistantCaseDetail }) => (
  <Box
    data-testid="assistant-case-summary"
    sx={{
      p: 2,
      borderRadius: 1,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {ROLE_LABELS[data.role]}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Typography
        variant="subtitle1"
        sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {data.title}
      </Typography>
      {data.status && (
        <Chip size="small" variant="outlined" label={data.status} />
      )}
    </Box>
    {data.statusLine && (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {data.statusLine}
      </Typography>
    )}
    <Typography variant="caption" color="text.secondary" component="p">
      {[
        data.practiceArea,
        data.jurisdiction,
        formatAssistantFee(data.fee),
        data.matterStage,
      ]
        .filter(Boolean)
        .join(' · ')}
    </Typography>
    {data.proposals.length > 0 && (
      <>
        <Divider sx={{ my: 1 }} />
        <Typography variant="overline" color="text.secondary">
          Proposals
        </Typography>
        {data.proposals.map(proposal => (
          <ProposalRow key={proposal.proposalId} proposal={proposal} />
        ))}
      </>
    )}
    <Button
      component={RouterLink}
      to={assistantCasePath(data.caseId)}
      size="small"
      variant="outlined"
      sx={{ mt: 1.5 }}
    >
      Open case
    </Button>
  </Box>
)

export default AssistantCaseSummary
